import type { IntlShape } from 'react-intl'
import type { TripPlannerEvent } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { formatDayLabel, getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import type { ChatInputContext } from '../hooks/useDrawerAskAIComposerContext'

type AskAIDayEvent = Extract<TripPlannerEvent, { type: 'ask_ai_for_day' }>
type AskAIItemEvent = Extract<TripPlannerEvent, { type: 'ask_ai_for_item' }>
type AskAIListRowEvent = Extract<TripPlannerEvent, { type: 'ask_ai_for_list_row' }>

export function buildAskAIContextForDay(
  intl: IntlShape,
  event: AskAIDayEvent,
): ChatInputContext {
  const dayLabel = event.date
    ? `${formatDayLabel(event.date)} (${getLocalisedDayLabel(intl, event.dayNumber)})`
    : getLocalisedDayLabel(intl, event.dayNumber)
  const label = intl.formatMessage(
    {
      id: 'WWQHyN9c4jvJQITwN7jQ',
      defaultMessage: '{dayLabel} - {title}',
      description: 'Day context bar label showing day label and title in trip planner chat',
    },
    { dayLabel, title: event.title },
  )
  const placeholder = intl.formatMessage(
    {
      id: '7eTuFAD860P0YRHQhFcJ',
      defaultMessage: 'Ask me anything about {dayLabel}',
      description: 'Chat input placeholder when user is asking about a specific day in the trip itinerary',
    },
    { dayLabel: getLocalisedDayLabel(intl, event.dayNumber, event.date) },
  )

  return {
    label,
    message: `The user is asking about ${dayLabel}: ${event.title}`,
    placeholder,
  }
}

export function buildAskAIContextForItem(
  intl: IntlShape,
  event: AskAIItemEvent,
): ChatInputContext {
  const placeholder = intl.formatMessage(
    {
      id: 'HsaECJgtZ2iQBD6C8nTE',
      defaultMessage: 'Ask me anything about {name}',
      description: 'Chat input placeholder when the user is asking about a specific itinerary item in the trip planner',
    },
    { name: event.name },
  )

  return {
    label: event.name,
    message: event.itemId !== undefined
      ? JSON.stringify({ itemId: event.itemId, name: event.name, category: event.category, description: event.description, locationRefinementPrompt: event.locationRefinementPrompt, action: event.action ?? 'ask_about_item' })
      : `The user is asking about ${event.name}${event.category ? ` (${event.category})` : ''}`,
    placeholder,
  }
}

export function buildAskAIContextForListRow(
  intl: IntlShape,
  event: AskAIListRowEvent,
): ChatInputContext {
  const placeholder = intl.formatMessage(
    {
      id: 'FX7E5Rf2eGbjg4NB328V',
      defaultMessage: 'Ask me anything about {title}',
      description: 'Chat input placeholder when user is asking about a specific row in the trip planner list view',
    },
    { title: event.title },
  )

  return {
    label: event.title,
    message: event.context,
    placeholder: event.placeholder ?? placeholder,
  }
}
